<template>
  <input-popover v-model:open="showDropdown" :position="position" :offset="offset">
    <template #trigger>
      <div>
        <slot name="trigger" :label="formatter(selectedOption)" :show-dropdown="showDropdown">
          <div
            class="flex cursor-pointer items-center justify-between rounded-xs border border-gray-800 p-1 text-gray-600"
            data-cy="defaultDropdownTrigger"
          >
            <span>
              {{ formatter(selectedOption) }}
            </span>
            <icon-arrow :class="showDropdown ? '-rotate-90' : 'rotate-90'" />
          </div>
        </slot>
      </div>
    </template>
    <template #content>
      <div class="relative">
        <ul
          class="max-h-80 overflow-y-auto bg-white shadow-md"
          :class="{
            'p-2': styleOptionsWithDefault.menuPadded,
            'relative top-2': styleOptionsFromProps.menuGap,
          }"
        >
          <li
            v-for="optionItem in options"
            :key="optionItem.id"
            @click="selectOption(optionItem.id)"
          >
            <slot name="option" v-bind="optionItem" :active="modelValue === optionItem.id">
              <div
                class="w-full cursor-pointer p-2 text-gray-600 hover:bg-blue-100"
                data-cy="defaultDropdownContent"
              >
                {{ formatter(optionItem) }}
              </div>
            </slot>
          </li>
        </ul>
      </div>
    </template>
  </input-popover>
</template>

<script lang="ts">
export type Option = {
  id: string | number;
};
</script>

<script setup lang="ts" generic="K extends Option">
import { ref, computed, type PropType } from "vue";
import InputPopover from "@/components/input/InputPopover.vue";
import { type VariationPlacement, type BasePlacement } from "@popperjs/core";
import IconArrow from "@/components/icons/IconArrow.vue";
import merge from "lodash/merge";

const props = defineProps({
  options: {
    type: Array as PropType<Readonly<K[]>>,
    required: true,
  },
  position: {
    type: String as PropType<VariationPlacement | BasePlacement>,
    default: "bottom-start",
  },
  modelValue: {
    type: [String, Number],
    default: "",
  },
  formatter: {
    type: Function as PropType<(option: K | undefined) => string | number>,
    default: (option: K | undefined) => option?.id ?? "",
  },
  offset: {
    type: Array as PropType<number[]>,
    default: () => [0, 0],
  },
  styleOptions: {
    type: Object as PropType<StyleOptions>,
    default: undefined,
  },
});

const defaultStyles = {
  menuPadded: true,
  menuGap: false,
} as const;

const styleOptionsFromProps = { ...props.styleOptions };
const styleOptionsWithDefault = merge(props.styleOptions, defaultStyles);

interface StyleOptions {
  menuPadded?: boolean;
  menuGap?: boolean;
}

const emit = defineEmits<{
  "update:modelValue": [id: string | number];
}>();

const showDropdown = ref(false);

const selectedOption = computed(() => {
  return props.options.find((option) => option.id === props.modelValue);
});

const selectOption = (id: string | number) => {
  emit("update:modelValue", id);
  showDropdown.value = false;
};
</script>
